import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getSettingsRequest } from "../../../redux/settings/settingsSlice";
import { saveRegistrationGiftRequest } from "../../../redux/settings/settingsSlice";
import { getMetalsRequest } from "../../../redux/GetMetals/metalSlice";
import AccessControl from "../../Comman/AccessControl";
import { controlAllFeatureOptions } from "../../../configs/constants";
import { canShowPermission } from "../Permissions/permissionObject";

const RegistrationGift = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.settingData);
  const decimalPrecision =
    useSelector((state) => state?.appConfig?.configData?.decimalPrecision) ?? 4;
  const metalsdata = data?.data?.metals || [];

  const [isEnabledRegistration, setIsEnabledRegistration] = useState(false);
  const [metalRegistration, setMetalRegistration] = useState("");
  const [weightRegistration, setWeightRegistration] = useState("");
  const [validityDaysRegistration, setValidityDaysRegistration] = useState("");
  const [typeRegistration, setTypeRegistration] = useState("weight");
  const [startDateRegistration, setStartDateRegistration] = useState(null);
  const [endDateRegistration, setEndDateRegistration] = useState(null);
  const [isSavingRegistrationGift, setIsSavingRegistrationGift] =
    useState(false);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);

  const canCreateSetting = canShowPermission(permissions, "registrationGift", "create");
  const canEdit = canShowPermission(permissions, "registrationGift", "edit");

  useEffect(() => {
    dispatch(getSettingsRequest());
    dispatch(getMetalsRequest());
  }, [dispatch]);

  useEffect(() => {
    const RegistrationgiftingData =
      data?.data?.registrationgiftdetails || {};
    setIsEnabledRegistration(
      RegistrationgiftingData?.registrationgiftstatus === true ||
        RegistrationgiftingData?.registrationgiftstatus === "true" ||
        false
    );
    setWeightRegistration(
      RegistrationgiftingData?.weight ??
        RegistrationgiftingData?.amount ??
        ""
    );
    setValidityDaysRegistration(RegistrationgiftingData?.validity || "");
    setMetalRegistration(RegistrationgiftingData?.bullion || "");
    setTypeRegistration(RegistrationgiftingData?.type || "weight");
    setStartDateRegistration(
      RegistrationgiftingData?.startDate
        ? new Date(RegistrationgiftingData.startDate)
        : null
    );
    setEndDateRegistration(
      RegistrationgiftingData?.endDate
        ? new Date(RegistrationgiftingData.endDate)
        : null
    );
  }, [data]);

  const handleRegistrationgiftdetails = async () => {
    if (isSavingRegistrationGift) return;

    const trimmedWeight = String(weightRegistration).trim();
    const trimmedValidityDays = String(validityDaysRegistration).trim();
    const trimmedMetal = String(metalRegistration).trim();
    const trimmedType = String(typeRegistration).trim();

    if (
      startDateRegistration &&
      endDateRegistration &&
      endDateRegistration < startDateRegistration
    ) {
      toast.error("End date cannot be earlier than start date.");
      return;
    }

    if (!trimmedWeight) {
      toast.error(
        trimmedType === "value" ? "Amount is required." : "Weight is required."
      );
      return;
    }
    if (Number(trimmedWeight) <= 0) {
      toast.error(
        trimmedType === "value"
          ? "Amount must be greater than 0."
          : "Weight must be greater than 0."
      );
      return;
    }
    if (!trimmedValidityDays) {
      toast.error("Validity days are required.");
      return;
    }
    if (!trimmedMetal) {
      toast.error("Please select a metal.");
      return;
    }
    if (!trimmedType) {
      toast.error("Please select type");
      return;
    }

    setIsSavingRegistrationGift(true);

    const payload = {
      key: "registrationgiftdetails",
      value: {
        registrationgiftstatus: isEnabledRegistration,
        ...(trimmedType === "value"
          ? { amount: trimmedWeight }
          : { weight: trimmedWeight }),
        validity: trimmedValidityDays.toString(),
        bullion: trimmedMetal,
        type: trimmedType,
        startDate: startDateRegistration,
        endDate: endDateRegistration,
      },
    };

    try {
      await dispatch(saveRegistrationGiftRequest(payload));
    } catch (err) {
      console.error("Failed to save:", err);
      toast.error("Failed to save registration gift details.");
    } finally {
      setTimeout(() => {
        setIsSavingRegistrationGift(false);
      }, 2500);
    }
  };

  return (
    <AccessControl feature={controlAllFeatureOptions.Registration_Gift}>
      <Box sx={{ width: "100%", mt: { xs: 10, md: 10 }, mb: { xs: 2, md: 2 } }}>
        <Paper
          elevation={2}
          sx={{ p: 3, width: "100%", border: "1px solid #ccc" }}
        >
          <Typography
            fontWeight={700}
            fontSize={24}
            mb={3}
            fontFamily={"Aeonik"}
          >
            Gifting on registration
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={isEnabledRegistration}
                onChange={(e) => setIsEnabledRegistration(e.target.checked)}
              />
            }
            label="Enable functionality"
            sx={{ mb: 3 }}
          />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                label={
                  <>
                    {typeRegistration === "value"
                      ? "Enter Amount"
                      : "Enter Weight"}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </>
                }
                value={weightRegistration}
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/[^0-9.]/g, "");
                  const parts = value.split(".");
                  if (parts.length > 2) {
                    value = parts[0] + "." + parts[1];
                  }
                  if (
                    value.startsWith("0") &&
                    !value.startsWith("0.") &&
                    value.length > 1
                  ) {
                    value = value.replace(/^0+/, "");
                  }
                  const maxDecimals =
                    typeRegistration === "value"
                      ? 2
                      : Math.max(0, Number(decimalPrecision) || 4);
                  if (parts[1]?.length > maxDecimals) {
                    value = parts[0] + "." + parts[1].slice(0, maxDecimals);
                  }
                  setWeightRegistration(value);
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label={
                  <>
                    Validity Days <span style={{ color: "red" }}>*</span>
                  </>
                }
                value={validityDaysRegistration}
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/\D/g, "");
                  value = value.replace(/^0+/, "");
                  setValidityDaysRegistration(value);
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>
                  Select Metal <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  value={metalRegistration}
                  label="Select Metal"
                  onChange={(e) => setMetalRegistration(e.target.value)}
                >
                  {(metalsdata || [])
                    .filter((metal) => metal.status === true)
                    .map((metalItem) => (
                      <MenuItem key={metalItem._id} value={metalItem._id}>
                        {metalItem.metalName} ({metalItem.karat}k -{" "}
                        {metalItem.purity})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>
                  Select Type <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  value={typeRegistration}
                  label="Select Type"
                  onChange={(e) => setTypeRegistration(e.target.value)}
                >
                  <MenuItem value="weight">Weight</MenuItem>
                  <MenuItem value="value">Value</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  sx={{ width: "100%" }}
                  label="Start Offer Date"
                  value={startDateRegistration}
                  onChange={(newValue) =>
                    setStartDateRegistration(newValue)
                  }
                  format="dd/MM/yyyy hh:mm a"
                  renderInput={(params) => (
                    <TextField fullWidth {...params} />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  sx={{ width: "100%" }}
                  label="End Offer Date"
                  value={endDateRegistration}
                  onChange={(newValue) => setEndDateRegistration(newValue)}
                  format="dd/MM/yyyy hh:mm a"
                  renderInput={(params) => (
                    <TextField fullWidth {...params} size="small" />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Box mt={4}>
            {(canCreateSetting || canEdit) && (
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#752ADA",
                  px: 6,
                  textTransform: "none",
                }}
                onClick={handleRegistrationgiftdetails}
                disabled={isSavingRegistrationGift}
              >
                {isSavingRegistrationGift ? "Saving..." : "Save"}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </AccessControl>
  );
};

export default RegistrationGift;
